import type {
  IChartApi,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  SeriesType,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';

type RenderTarget = Parameters<NonNullable<IPrimitivePaneRenderer['draw']>>[0];

export interface GapLine {
  /** Open time of the bar that gapped, on the instrument's stored clock. */
  time: number;
  level: number;
  filled: boolean;
  /** Open time of the bar that closed it, or null while it stands. */
  filledTime: number | null;
}

/** Gap colours by age. See `ageOf` for where the boundaries fall. */
export interface GapColors {
  today: string;
  week: string;
  older: string;
}

export interface GapDrawing {
  gaps: GapLine[];
  /** The loaded window, so a line can be clamped to candles that exist. */
  firstTime: number;
  lastTime: number;
  /** Now, on the same stored clock the gap times use. */
  nowStored: number;
  colors: GapColors;
}

const WEEK_SEC = 7 * 86_400;

/**
 * Which age band a gap falls in.
 *
 * Today is a calendar comparison, not "within 24 hours": a gap from this
 * morning and one from last night are both today's business, while one from
 * 23 hours ago on the previous date is not. The stored clock carries the
 * broker offset already, so reading it with UTC getters gives the broker
 * calendar — the same dates the chart axis shows.
 */
function ageOf(timeStored: number, nowStored: number): keyof GapColors {
  const d = new Date(timeStored * 1000);
  const n = new Date(nowStored * 1000);
  if (
    d.getUTCFullYear() === n.getUTCFullYear() &&
    d.getUTCMonth() === n.getUTCMonth() &&
    d.getUTCDate() === n.getUTCDate()
  ) {
    return 'today';
  }
  return nowStored - timeStored < WEEK_SEC ? 'week' : 'older';
}

/**
 * Gap levels, drawn as horizontal rays coloured by age.
 *
 * Each line starts at the bar that gapped and runs right: to the bar that
 * closed it if it was filled, or to the newest bar if it still stands. A
 * filled one is thin, dashed and dimmed, so it reads as history rather than
 * competing with the levels still in play.
 *
 * This replaced one LineSeries per gap. A month of M1 history holds about
 * 2,500 gaps once the filled ones are included, and that many series makes
 * the chart unusable — where a single canvas pass does not care. It also puts
 * every gap on one z-layer, so a fresh level can never end up drawn beneath
 * an older one.
 */
export class GapPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: ISeriesApi<SeriesType> | null = null;
  private requestUpdate: (() => void) | null = null;
  private drawing: GapDrawing | null = null;
  private readonly view: IPrimitivePaneView;

  constructor() {
    const renderer: IPrimitivePaneRenderer = {
      draw: (target: RenderTarget) => this.render(target),
    };

    /*
     * Above the candles, unlike the knot primitive.
     *
     * A gap level is a price the market is expected to come back to, so it is
     * read against the candles rather than behind them — and beneath them a
     * line vanishes exactly where price has traded, which is where it is most
     * worth seeing.
     */
    this.view = {
      zOrder: (): PrimitivePaneViewZOrder => 'top',
      renderer: () => renderer,
    };
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart as IChartApi;
    this.series = param.series as ISeriesApi<SeriesType>;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  setDrawing(drawing: GapDrawing | null): void {
    this.drawing = drawing;
    this.requestUpdate?.();
  }

  private render(target: RenderTarget): void {
    const chart = this.chart;
    const series = this.series;
    const drawing = this.drawing;
    if (!chart || !series || !drawing || drawing.gaps.length === 0) return;

    const timeScale = chart.timeScale();

    /*
     * What is on screen, in stored time, read once per frame. Comparing times
     * before converting any coordinate keeps the cost of a pan proportional to
     * the gaps actually in view rather than to every gap in the window.
     */
    const visible = timeScale.getVisibleRange();
    const from = visible === null ? null : (visible.from as number);
    const to = visible === null ? null : (visible.to as number);

    target.useBitmapCoordinateSpace(({ context, bitmapSize, horizontalPixelRatio, verticalPixelRatio }) => {
      for (const gap of drawing.gaps) {
        /*
         * Clamped to the loaded window. A gap older than the first candle has
         * no bar on this chart to start from, so its line begins at the left
         * edge instead — it is still a live level, just one formed off screen.
         */
        const startT = Math.max(gap.time, drawing.firstTime);
        const endT =
          gap.filled && gap.filledTime !== null
            ? Math.min(Math.max(gap.filledTime, drawing.firstTime), drawing.lastTime)
            : drawing.lastTime;
        if (endT < startT) continue;

        if (from !== null && to !== null && (endT < from || startT > to)) continue;

        const y = series.priceToCoordinate(gap.level);
        if (y === null) continue;
        const x1 = timeScale.timeToCoordinate(startT as UTCTimestamp);
        const x2 = timeScale.timeToCoordinate(endT as UTCTimestamp);
        if (x1 === null || x2 === null) continue;

        const left = x1 * horizontalPixelRatio;
        // At least a pixel wide: a gap filled by the very next bar is a real
        // event and should not render as nothing.
        const right = Math.max(left + 1, x2 * horizontalPixelRatio);
        if (right < 0 || left > bitmapSize.width) continue;

        const py = Math.round(y * verticalPixelRatio) + 0.5;

        context.save();
        context.strokeStyle = drawing.colors[ageOf(gap.time, drawing.nowStored)];
        context.lineWidth = Math.max(1, Math.round((gap.filled ? 1 : 2) * verticalPixelRatio));
        if (gap.filled) {
          context.globalAlpha = 0.45;
          context.setLineDash([4 * horizontalPixelRatio, 4 * horizontalPixelRatio]);
        }
        context.beginPath();
        context.moveTo(left, py);
        context.lineTo(right, py);
        context.stroke();
        context.restore();
      }
    });
  }
}
